/**
 * Consejos de Gavi para los pagos fijos (v1: seguros de coche, hogar y
 * salud; luz y gas; móvil y fibra; suscripciones; tarjeta y cuenta). Regla
 * de Vadik (21-oct-2026): ahorrar con lo que ya tienes, nunca cambiar de
 * compañía. Cada consejo sale de los números de la persona y su texto va en
 * `useTextoConsejo`. Disparadores y fuentes:
 * notas/pagos-fijos-gavi/sintesis-director.md §1, §3 y §6. Funciones puras.
 */
import { diasEntre } from '#components/mobile/budget/pagosFijos';

import {
  claveComercio,
  comparaConHaceUnAnio,
  mesesDesde,
  proximoAniversario,
  subidaDe,
  subidaNotable,
  sumarDias,
  sumarMeses,
} from './cobros';
import type { CargoGavi, SerieCobro } from './cobros';
import type { Contrato, EstadoGavi } from './estadoGavi';
import {
  esConsumo,
  esSeguro,
  normalizarComercio,
  reconocerTipo,
} from './tiposRecibo';
import type { TipoRecibo } from './tiposRecibo';

export type IdConsejo =
  | 'baja-vuelve'
  | 'contrato-promo'
  | 'contrato-permanencia'
  | 'seguro-renovacion'
  | 'seguro-subida'
  | 'seguro-fraccionado'
  | 'coche-cobertura'
  | 'consumo-subida'
  | 'primer-anio'
  | 'retencion'
  | 'luz-potencia'
  | 'consumo-extra'
  | 'tele-subida'
  | 'tele-baila'
  | 'tele-plataforma'
  | 'susc-renovacion'
  | 'susc-subida'
  | 'susc-coste-anual'
  | 'tarjeta-aplazada'
  | 'cuenta-comision';

/**
 * Prioridad (síntesis §3): 1 = fecha límite o cobro tras la baja; 2 = subida
 * detectada; 3 = lo que se cuela; 4 = el resto, solo en la ficha.
 */
export type Prioridad = 1 | 2 | 3 | 4;

export type Consejo = {
  id: IdConsejo;
  /** Única por disparador (`serie|id|instancia`): lo que se calla o se avisa. */
  clave: string;
  /** Serie de la que habla. */
  serie: string;
  categoria: string;
  tipo: TipoRecibo | null;
  prioridad: Prioridad;
  /** ¿Puede ir en la notificación de la semana? */
  aviso: boolean;
  /** Pregunta de Gavi (clave de la respuesta) antes de aconsejar. */
  pregunta?: string;
  datos: DatosConsejo;
};

export type DatosConsejo = {
  comercio: string;
  /** Céntimos. */
  importe: number;
  anual: number;
  antes?: number;
  ahora?: number;
  /** +0,12 = un 12 % más. */
  cambio?: number;
  /** Fecha del consejo (renovación, fin de promoción…). */
  fecha?: string;
  meses?: number;
  anios?: number;
  cuotas?: number;
  extra?: number;
  otro?: string;
  minimo?: number;
  maximo?: number;
  basica?: boolean;
};

export type EntradaConsejos = {
  categoria: string;
  nombreCategoria: string;
  /** Series de esta categoría, la de más dinero primero. */
  series: readonly SerieCobro[];
  contrato: Contrato | undefined;
};

export type ContextoConsejos = {
  hoy: string;
  estado: EstadoGavi;
  /** Todas las series vivas (para lo que se cuela y las plataformas). */
  todas: readonly SerieCobro[];
  /** Todos los cargos cargados (para «ha vuelto a cobrar»). */
  cargos: readonly CargoGavi[];
  /** Primer cargo conocido por cuenta (ver `inicioPorCuenta`). */
  inicioCuenta: ReadonlyMap<string, string>;
};

/** Emisores de tarjetas de pago aplazado. */
const EMISORES_APLAZADO = [
  'wizink',
  'cofidis',
  'oney',
  'cetelem',
  'carrefour pass',
  'pass carrefour',
  'bankinter consumer',
];
const OPERADORAS_CON_PLATAFORMAS = ['movistar', 'orange', 'vodafone'];
const PLATAFORMAS = [
  'netflix',
  'disney',
  'hbo',
  'max',
  'prime video',
  'amazon prime',
];

function tiene(comercio: string, lista: readonly string[]): boolean {
  const c = normalizarComercio(comercio);
  return lista.some(p => c.includes(` ${p} `));
}

/** Tipo de una serie: el del contrato (si es la principal) o el reconocido. */
export function tipoDeSerie(
  serie: SerieCobro,
  nombreCategoria: string | null,
  contrato?: Contrato,
): TipoRecibo | null {
  return contrato?.tipo ?? reconocerTipo(serie.comercio, nombreCategoria);
}

/**
 * ¿Lo que se ve de la serie es su principio de verdad? Si la cuenta tiene
 * cargos de al menos dos meses antes, sí; si el historial empieza ahí, no
 * se sabe cuánto lleva.
 */
export function principioFiable(
  serie: SerieCobro,
  inicioCuenta: ReadonlyMap<string, string>,
): boolean {
  const inicio = serie.cuenta ? inicioCuenta.get(serie.cuenta) : undefined;
  return inicio != null && diasEntre(inicio, serie.desde) >= 60;
}

/**
 * Día en que se renueva: el del contrato o, si no, el aniversario del cargo
 * anual o del primer cargo de un seguro (si ese principio es fiable).
 */
export function fechaRenovacion(
  serie: SerieCobro,
  tipo: TipoRecibo | null,
  contrato: Contrato | undefined,
  hoy: string,
  inicioCuenta: ReadonlyMap<string, string>,
): string | null {
  if (contrato?.renueva) {
    return proximoAniversario(contrato.renueva, hoy);
  }
  if (serie.cada === 12) {
    return proximoAniversario(serie.siguiente, hoy);
  }
  if (esSeguro(tipo) && principioFiable(serie, inicioCuenta)) {
    return proximoAniversario(serie.desde, hoy);
  }
  return null;
}

function aniosCon(
  serie: SerieCobro,
  hoy: string,
  inicioCuenta: ReadonlyMap<string, string>,
): number | undefined {
  // Sin principio fiable, al menos lo que se ve.
  const anios = Math.floor(mesesDesde(serie.desde, hoy) / 12);
  return anios >= 1 && (principioFiable(serie, inicioCuenta) || anios >= 2)
    ? anios
    : undefined;
}

/** Clave de la pregunta «¿Te avisaron de una subida?» de este año. */
export function clavePreguntaSubida(serie: string, hoy: string): string {
  return `${serie}|consumo-subida|${hoy.slice(0, 4)}`;
}

/** Clave de la pregunta «¿Tu coche tiene más de 5 años?». */
export function clavePreguntaCoche(serie: string): string {
  return `${serie}|coche-anios`;
}

/** Consejos de un pago fijo, sin filtrar los silenciados. */
export function consejosDe(
  entrada: EntradaConsejos,
  ctx: ContextoConsejos,
): Consejo[] {
  const { hoy, estado } = ctx;
  const r: Consejo[] = [];
  const principal = entrada.series[0];

  // Cobros después de «Lo he dado de baja» (con 5 días de margen para el
  // último recibo).
  for (const [clave, fecha] of Object.entries(estado.bajas)) {
    // Por nombre, no por id: el banco puede volver a cobrar con otro nombre.
    const payee = clave.split('#')[0];
    const comercio = claveComercio(
      ctx.cargos.find(c => c.payee === payee)?.comercio ?? null,
      payee,
    );
    const cobro = ctx.cargos
      .filter(
        c =>
          c.payee != null &&
          claveComercio(c.comercio, c.payee) === comercio &&
          c.categoria === entrada.categoria &&
          c.fecha > sumarDias(fecha, 5),
      )
      .sort((a, b) => b.fecha.localeCompare(a.fecha))[0];
    if (cobro) {
      r.push({
        id: 'baja-vuelve',
        clave: `${clave}|baja-vuelve|${cobro.fecha}`,
        serie: clave,
        categoria: entrada.categoria,
        tipo: null,
        prioridad: 1,
        aviso: true,
        datos: {
          comercio: cobro.comercio ?? '',
          importe: cobro.importe,
          anual: 0,
          fecha: cobro.fecha,
        },
      });
    }
  }

  if (!principal) {
    return r;
  }
  const tipo = tipoDeSerie(
    principal,
    entrada.nombreCategoria,
    entrada.contrato,
  );
  const s = principal.clave;
  const base = {
    serie: s,
    categoria: entrada.categoria,
    tipo,
  };
  const datos: DatosConsejo = {
    comercio: principal.comercio,
    importe: principal.importe,
    anual: principal.anual,
  };
  const push = (c: Omit<Consejo, 'serie' | 'categoria' | 'tipo'>) =>
    r.push({ ...base, ...c });

  // Fechas del contrato (cualquier tipo).
  const contrato = entrada.contrato;
  if (contrato?.finPromo) {
    const dias = diasEntre(hoy, contrato.finPromo);
    if (dias >= 0 && dias <= 30) {
      push({
        id: 'contrato-promo',
        clave: `${s}|contrato-promo|${contrato.finPromo}`,
        prioridad: 1,
        aviso: true,
        datos: { ...datos, fecha: contrato.finPromo },
      });
    }
  }
  if (contrato?.permanencia) {
    const dias = diasEntre(hoy, contrato.permanencia);
    if (dias >= -30 && dias <= 14) {
      push({
        id: 'contrato-permanencia',
        clave: `${s}|contrato-permanencia|${contrato.permanencia}`,
        prioridad: 1,
        aviso: true,
        datos: { ...datos, fecha: contrato.permanencia },
      });
    }
  }

  if (tipo == null || tipo === 'otro') {
    return r;
  }

  const subida = subidaDe(principal);
  const diasSubida = subida ? diasEntre(subida.fecha, hoy) : Infinity;
  const anios = aniosCon(principal, hoy, ctx.inicioCuenta);

  if (esSeguro(tipo)) {
    const renueva = fechaRenovacion(
      principal,
      tipo,
      contrato,
      hoy,
      ctx.inicioCuenta,
    );
    if (renueva) {
      const dias = diasEntre(hoy, renueva);
      const salud = tipo === 'seguro-salud';
      const [desde, hasta, avisoDesde] = salud ? [14, 75, 60] : [30, 60, 42];
      if (dias > desde && dias <= hasta) {
        push({
          id: 'seguro-renovacion',
          clave: `${s}|seguro-renovacion|${renueva}`,
          prioridad: 1,
          aviso: dias <= avisoDesde,
          datos: {
            ...datos,
            fecha: renueva,
            anios,
            antes: subida?.antes,
            ahora: subida?.ahora,
          },
        });
      }
    }
    const reciente = principal.cada === 12 ? 365 : 120;
    if (
      subida &&
      subida.ahora >= subida.antes * 1.05 &&
      diasSubida <= reciente
    ) {
      push({
        id: 'seguro-subida',
        clave: `${s}|seguro-subida|${subida.fecha}`,
        prioridad: 2,
        aviso: true,
        datos: {
          ...datos,
          antes: subida.antes,
          ahora: subida.ahora,
          cambio: subida.ahora / subida.antes - 1,
          anios,
        },
      });
    }
    if (tipo !== 'seguro-salud' && principal.cada < 12) {
      push({
        id: 'seguro-fraccionado',
        clave: `${s}|seguro-fraccionado|${hoy.slice(0, 4)}`,
        prioridad: 4,
        aviso: false,
        datos: { ...datos, cuotas: 12 / principal.cada },
      });
    }
    if (tipo === 'seguro-coche' && (anios == null || anios >= 2)) {
      push({
        id: 'coche-cobertura',
        clave: `${s}|coche-cobertura`,
        prioridad: 4,
        aviso: false,
        pregunta: clavePreguntaCoche(s),
        datos,
      });
    }
  }

  if (esConsumo(tipo)) {
    const interanual = comparaConHaceUnAnio(principal, hoy);
    if (interanual && interanual.cambio >= 0.1) {
      push({
        id: 'consumo-subida',
        clave: clavePreguntaSubida(s, hoy),
        prioridad: 2,
        aviso: true,
        pregunta: clavePreguntaSubida(s, hoy),
        datos: {
          ...datos,
          antes: interanual.antes,
          ahora: interanual.ahora,
          cambio: interanual.cambio,
          anual: interanual.alAnio,
        },
      });
    }
    // Lo que se cuela: otro cobro pequeño del mismo comercio.
    const extra = ctx.todas.find(
      o =>
        o.payee === principal.payee &&
        o.clave !== principal.clave &&
        o.importe <= 2000,
    );
    if (extra) {
      push({
        id: 'consumo-extra',
        clave: `${extra.clave}|consumo-extra`,
        prioridad: 3,
        aviso: false,
        datos: { ...datos, extra: extra.importe, anual: extra.anual },
      });
    }
    if (tipo === 'luz' && principal.cargos.length >= 6) {
      push({
        id: 'luz-potencia',
        clave: `${s}|luz-potencia|${hoy.slice(0, 4)}`,
        prioridad: 4,
        // Una vez al año, en octubre.
        aviso: hoy.slice(5, 7) === '10',
        datos,
      });
    }
  }

  if (esConsumo(tipo) || tipo === 'tele') {
    if (principioFiable(principal, ctx.inicioCuenta)) {
      const dias = diasEntre(principal.desde, hoy);
      const aniversario = sumarMeses(principal.desde, 12);
      if (dias >= 305 && dias <= 350 && principal.cargos.length >= 6) {
        push({
          id: 'primer-anio',
          clave: `${s}|primer-anio|${aniversario}`,
          prioridad: 1,
          aviso: true,
          datos: { ...datos, fecha: aniversario, meses: 11 },
        });
      } else if (tipo === 'tele' && dias >= 670 && dias <= 715) {
        push({
          id: 'retencion',
          clave: `${s}|retencion|${sumarMeses(principal.desde, 24)}`,
          prioridad: 3,
          aviso: true,
          datos: { ...datos, meses: 23 },
        });
      }
    }
  }

  if (tipo === 'tele') {
    if (subida && subidaNotable(subida) && diasSubida <= 120) {
      push({
        id: 'tele-subida',
        clave: `${s}|tele-subida|${subida.fecha}`,
        prioridad: 2,
        aviso: true,
        datos: {
          ...datos,
          antes: subida.antes,
          ahora: subida.ahora,
          anual: Math.round(
            ((subida.ahora - subida.antes) * 12) / principal.cada,
          ),
        },
      });
    } else {
      const ultimos = principal.cargos.slice(-3).map(c => c.importe);
      const min = Math.min(...ultimos);
      const max = Math.max(...ultimos);
      if (ultimos.length === 3 && max > min * 1.05) {
        push({
          id: 'tele-baila',
          clave: `${s}|tele-baila|${principal.cargos.at(-1)?.fecha}`,
          prioridad: 3,
          aviso: false,
          datos: { ...datos, minimo: min, maximo: max },
        });
      }
    }
    if (tiene(principal.comercio, OPERADORAS_CON_PLATAFORMAS)) {
      const plataforma = ctx.todas.find(o => tiene(o.comercio, PLATAFORMAS));
      if (plataforma) {
        push({
          id: 'tele-plataforma',
          clave: `${s}|tele-plataforma|${hoy.slice(0, 4)}`,
          prioridad: 4,
          aviso: false,
          datos: {
            ...datos,
            otro: plataforma.comercio,
            extra: plataforma.importe,
          },
        });
      }
    }
  }

  if (tipo === 'suscripcion') {
    if (principal.cada === 12) {
      const dias = diasEntre(hoy, principal.siguiente);
      if (dias >= 0 && dias <= 20) {
        push({
          id: 'susc-renovacion',
          clave: `${s}|susc-renovacion|${principal.siguiente}`,
          prioridad: 1,
          aviso: true,
          datos: { ...datos, fecha: principal.siguiente },
        });
      }
    }
    if (subida && subida.ahora >= subida.antes * 1.05 && diasSubida <= 120) {
      push({
        id: 'susc-subida',
        clave: `${s}|susc-subida|${subida.fecha}`,
        prioridad: 3,
        aviso: false,
        datos: { ...datos, antes: subida.antes, ahora: subida.ahora },
      });
    }
    const totalSuscripciones = ctx.todas
      .filter(o => reconocerTipo(o.comercio, null) === 'suscripcion')
      .reduce((t, o) => t + o.anual, 0);
    if (totalSuscripciones >= 30000) {
      push({
        id: 'susc-coste-anual',
        clave: `susc-coste-anual|${hoy.slice(0, 4)}`,
        prioridad: 4,
        aviso: false,
        datos: { ...datos, anual: totalSuscripciones },
      });
    }
  }

  if (tipo === 'tarjeta') {
    if (tiene(principal.comercio, EMISORES_APLAZADO)) {
      if (principal.cada === 1 && principal.cargos.length >= 3) {
        push({
          id: 'tarjeta-aplazada',
          clave: `${s}|tarjeta-aplazada`,
          prioridad: 2,
          aviso: true,
          datos,
        });
      }
    } else if (principal.importe <= 2500) {
      const alMes = principal.anual / 12;
      push({
        id: 'cuenta-comision',
        clave: `${s}|cuenta-comision|${hoy.slice(0, 4)}`,
        prioridad: 3,
        // El resumen anual de comisiones llega en enero.
        aviso: hoy.slice(5, 7) === '01',
        datos: { ...datos, basica: alMes > 300 },
      });
    }
  }

  return r;
}

/**
 * Lo que se enseña: sin los silenciados («Ya he llamado», «No»), las
 * preguntas respondidas con «No» y, lo que tocaba recordar, de vuelta como
 * aviso con la máxima prioridad. Ordenados por prioridad.
 */
export function consejosVisibles(
  consejos: readonly Consejo[],
  estado: EstadoGavi,
  hoy: string,
): Consejo[] {
  const r: Consejo[] = [];
  for (const c of consejos) {
    const silencio = estado.silencios[c.clave];
    if (silencio && silencio > hoy) {
      continue;
    }
    if (c.id === 'coche-cobertura' && c.pregunta) {
      if (estado.respuestas[c.pregunta] === 'no') {
        continue;
      }
    }
    const recordar = estado.recordar[c.clave];
    if (recordar && recordar <= hoy) {
      r.push({ ...c, prioridad: 1, aviso: true });
    } else {
      r.push(c);
    }
  }
  return r.sort((a, b) => a.prioridad - b.prioridad);
}

/** Clave del aviso (cambia al recordarlo, para que vuelva a sonar). */
export function claveAviso(c: Consejo, estado: EstadoGavi): string {
  const recordar = estado.recordar[c.clave];
  return recordar ? `${c.clave}|r${recordar}` : c.clave;
}

/**
 * Candidatos a la notificación de la semana, de más a menos urgente. El
 * service worker da el primero que no haya dado ya, como mucho uno por
 * semana.
 */
export function avisosCandidatos(
  visibles: readonly Consejo[],
  max = 5,
): Consejo[] {
  return visibles.filter(c => c.aviso).slice(0, max);
}
